"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { useRouter } from "next/navigation";
import type { Post } from "@/types";
import type { PostFilters, PostsResponse } from "@/services";
import { fetchPosts, fetchPostsByCategory, fetchPostsByFollowing, fetchPostsByTag } from "@/services";
import PostCard from "./PostCard";
import PostModal from "./detail/ClientPostDetail";
import useDocStore from "@/models/doc";
import useStatusStore from "@/models/status";
import { track } from "@/lib/log";
import { usePostExposure } from "@/hooks";
import { configResponsive, useResponsive } from 'ahooks';
import MobilePostPage from "./detail/MobilePostDetail";

// 配置响应式断点
configResponsive({
  small: 0,
  middle: 640,
  large: 1024,
});

interface InfiniteFeedProps {
  initialPosts: Post[];
  hasMore: boolean;
  channel?: string;
  tagSlug?: string;
  basePath: string;
  initialPage?: number;
}

export default function InfiniteFeed({
  initialPosts,
  hasMore: initialHasMore,
  channel,
  tagSlug,
  basePath,
  initialPage = 1,
}: InfiniteFeedProps) {
  const router = useRouter();
  const responsive = useResponsive();
  const isMobile = responsive?.small === true && responsive?.middle === false && responsive?.large === false;

  // 防止水合错误：延迟渲染移动端模态框
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    setIsMounted(true);
  }, []);

  const { likedDocList } = useStatusStore();
  function checkLiked(docid: string) {
    return likedDocList.includes(docid);
  }

  const [posts, setPosts] = useState<Post[]>(initialPosts.map(post => ({ ...post, isLiked: checkLiked(post.doc_id), isNowLiked: checkLiked(post.doc_id) })));
  function safeSetPosts(setter: (prev: Post[]) => Post[] | Post | null) {
    setPosts((prev) => {
      if (typeof setter === "function") {
        const result = setter(prev);
        if (Array.isArray(result)) {
          return result.filter(Boolean);
        }
      }
      if (Array.isArray(setter)) {
        return setter.filter(Boolean);
      }
      return prev
    });
  }

  const [hasMore, setHasMore] = useState(initialHasMore);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(initialPage + 1);
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  // 使用曝光检测hooks
  const { exposedPosts, registerPostRef, resetExposure } = usePostExposure({
    posts,
    channel,
    basePath,
    threshold: 0.5,
    rootMargin: '0px'
  });

  // 加载更多数据的函数
  function loadMore(filters: PostFilters) {
    //记录当前页面所有的docid
    const docIds = posts.map(post => post.doc_id);
    console.log("🚀 ~ loadMore🚀 ~ loadMore🚀 ~ loadMore ~ filters:", filters)
    if (filters.channel) {
      return fetchPostsByCategory(filters.channel, filters.page, filters.limit, docIds);
    }
    if (tagSlug) {
      return fetchPostsByTag(tagSlug, filters.page, filters.limit);
    }
    if (basePath === "/following") {
      return fetchPostsByFollowing(filters.page, filters.limit);
    }
    return fetchPosts({ ...filters, docIds });
  }

  const handleLoadMore = useCallback(async () => {
    if (isLoading || !hasMore) return;

    setIsLoading(true);
    try {
      const result = await loadMore({ page, limit: 10, channel });
      safeSetPosts((prev) => [...prev, ...result.data.map(post => ({ ...post, isLiked: checkLiked(post.doc_id), isNowLiked: checkLiked(post.doc_id) }))]);
      setHasMore(result.has_more);
      setPage((prev) => prev + 1);
    } catch (error) {
      console.error("Failed to load more posts:", error);
    } finally {
      setIsLoading(false);
      //url query更新 page参数
      window.history.pushState({}, "", `${basePath}?page=${page}`);
    }
  }, [page, hasMore, isLoading, loadMore, channel, tagSlug]);

  useEffect(() => {
    if (!hasMore || isLoading) return;
    const observer = new window.IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          handleLoadMore();
          track("load_more", {
            type: "scroll",
            page: "list",
            action: "load_more",
            channel: channel,
            base_path: basePath
          })
        }
      },
      {
        root: null,
        rootMargin: "100px",
        threshold: 1.0,
      }
    );
    const current = loadMoreRef.current;
    if (current) {
      observer.observe(current);
    }
    return () => {
      if (current) observer.unobserve(current);
    };
  }, [handleLoadMore, hasMore, isLoading]);

  const { setDoc, clearDoc } = useDocStore();

  // 修改后的点击处理函数
  const handlePostClick = (post: Post) => {

    // 桌面端保持原来的Modal逻辑
    setSelectedPost(post);
    setIsModalOpen(true);
    track("post_modal", {
      action: "open",
      doc_id: post.doc_id,
      type: "click",
      page: "list",
      device: isMobile ? "mobile" : "desktop"
    });

    setDoc({
      docid: post.doc_id,
      title: post.title,
      content: post.content,
      authorId: post.author.id,
      tags: post.tags,
      images: post.images
    });

    // Update URL without reloading
    window.history.pushState({}, "", post.web_post_path);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedPost(null);
    clearDoc();
    // Restore URL
    window.history.pushState({}, "", basePath);
    track("post_modal", {
      action: "close",
      doc_id: selectedPost?.doc_id,
      type: "click",
      page: "list"
    });
  };
  const [disableAnimation, setDisableAnimation] = useState(false);
  // 监测到移动端滑动返回时，禁用动画
  useEffect(() => {
    if (!isMobile) return;

    let touchStartX = 0;
    let touchStartY = 0;
    let isEdgeSwipe = false;

    const handleTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      touchStartX = touch.clientX;
      touchStartY = touch.clientY;

      // 检测是否从左边缘开始（边缘阈值设为20px）
      isEdgeSwipe = touchStartX <= 20;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isEdgeSwipe) return;

      const touch = e.touches[0];
      const deltaX = touch.clientX - touchStartX;
      const deltaY = touch.clientY - touchStartY;

      // 检测是否是向右的水平滑动（水平滑动距离大于垂直滑动距离，且滑动距离超过30px）
      if (deltaX > 30 && Math.abs(deltaX) > Math.abs(deltaY)) {
        setDisableAnimation(true);
        handleCloseModal();
        setTimeout(() => {
          setDisableAnimation(false);
        }, 300);
      }
    };

    const handleTouchEnd = () => {
      isEdgeSwipe = false;
    };

    if (typeof window !== "undefined") {
      window.addEventListener("touchstart", handleTouchStart, { passive: true });
      window.addEventListener("touchmove", handleTouchMove, { passive: true });
      window.addEventListener("touchend", handleTouchEnd, { passive: true });
    }

    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("touchstart", handleTouchStart);
        window.removeEventListener("touchmove", handleTouchMove);
        window.removeEventListener("touchend", handleTouchEnd);
      }
    };
  }, [isMobile]);

  // 监测url回退时，关闭弹窗
  useEffect(() => {
    const handlePopState = () => {
      if (window.location.pathname === basePath && isModalOpen) {
        setDisableAnimation(true);
        handleCloseModal();
        setTimeout(() => {
          setDisableAnimation(false);
        }, 300);
      }
    };
    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [isModalOpen, handleCloseModal, basePath, disableAnimation]);

  const handleModalLike = useCallback((post: Post) => {
    safeSetPosts((prev) => prev.map(p => p.doc_id === post.doc_id ? post : p));
  }, []);

  const handleRefresh = useCallback(() => {
    resetExposure();
  }, [resetExposure]);

  return (
    <>
      <div className="min-h-screen">
        {(posts || []).map((post, index) => (
          <div
            key={`${post.doc_id}-${index}`}
            ref={(el) => registerPostRef(el, post.doc_id)}
            data-post-id={post.doc_id}
          >
            <PostCard
              post={post}
              onPostClick={handlePostClick}
            />
            {index !== posts.length - 1 && (
              <div className="my-3 h-[1px] bg-gray-200 w-full" style={{ color: 'rgba(0, 0, 0, 1)' }}></div>
            )}
          </div>
        ))}

        {/* Loading indicator */}
        {isLoading && (
          <div className="flex justify-center py-8">
            <div className="flex items-center space-x-2 text-gray-500">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-700"></div>
              <span className="text-sm">Loading more posts...</span>
            </div>
          </div>
        )}

        {/* No more posts indicator */}
        {!hasMore && posts?.length > 0 && (
          <div className="text-center py-8 text-gray-500">
            <div className="text-sm">You've reached the end!</div>
          </div>
        )}

        {/* Empty state */}
        {posts?.length === 0 && !isLoading && (
          <div className="text-center py-20 h-full flex flex-col items-center justify-center">
            <div className="text-gray-500 mb-4">
              <svg
                className="mx-auto h-12 w-12"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2 dark:text-white">
              No posts found
            </h3>
            <p className="text-gray-500 dark:text-white">Be the first to share something!</p>
          </div>
        )}

        {/* Load more button (fallback) */}
        {hasMore && !isLoading && (
          <div className="text-center py-8">
            <a href={`${basePath}?page=${page}`} onClick={e => {
              e.preventDefault();
              handleLoadMore();
            }}>
              <button className="btn-secondary">
                Load More
              </button>
            </a>
          </div>
        )}

        {/* IntersectionObserver 触发加载更多 */}
        {hasMore && !isLoading && (
          <div ref={loadMoreRef} style={{ height: 1 }} />
        )}
      </div>

      {/* Post Modal - 只在桌面端显示 */}
      {isMounted && !isMobile && isModalOpen && (
        <PostModal
          post={selectedPost as Post & { up: number, isLiked: boolean, isNowLiked: boolean }}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onLike={handleModalLike}
        />
      )}
      {isMounted && isMobile && (
        <div
          className={`
            fixed inset-0 z-50 bg-white dark:bg-black duration-300 ${disableAnimation ? "transition-none" : "transition-transform duration-300"}
            ${isModalOpen ? "translate-x-0" : "translate-x-full"}
          `}
          style={{ willChange: "transform" }}
          aria-modal="true"
          role="dialog"
        >
          <MobilePostPage
            post={selectedPost as Post & { up: number, isLiked: boolean, isNowLiked: boolean }}
            onBack={handleCloseModal}
            onLike={handleModalLike}
          />
        </div>
      )}
    </>
  );
}
